import { AlertTriangle, CheckCircle2, Clock3, XCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button, ProgressBar } from "../ui";

type CancelVoteStatus = "pending" | "passed" | "failed" | "expired";
type CancelVoteReason = "server_issue" | "game_bug" | "connection_issue" | "unfair_start" | "other";

type CancelVoteBannerProps = {
  voteId: string;
  initiatorName: string;
  status: CancelVoteStatus;
  reason: CancelVoteReason;
  reasonOtherText: string | null;
  yesCount: number;
  noCount: number;
  totalPlayers: number;
  requiredYesVotes: number;
  secondsRemaining: number | null;
  myVote: boolean | null;
  isCastingVote: boolean;
  onVote: (vote: boolean) => Promise<void>;
};

const reasonLabels: Record<CancelVoteReason, string> = {
  server_issue: "Server issue",
  game_bug: "Game-breaking bug",
  connection_issue: "Mass disconnect",
  unfair_start: "Unfair start",
  other: "Other",
};

export function CancelVoteBanner({
  voteId,
  initiatorName,
  status,
  reason,
  reasonOtherText,
  yesCount,
  noCount,
  totalPlayers,
  requiredYesVotes,
  secondsRemaining,
  myVote,
  isCastingVote,
  onVote,
}: CancelVoteBannerProps) {
  const totalVotes = yesCount + noCount;
  const progressValue = totalPlayers > 0 ? totalVotes : 0;
  const resultTitle =
    status === "passed"
      ? "Match cancelled by vote"
      : status === "failed"
        ? "Cancel vote failed"
        : status === "expired"
          ? "Cancel vote expired"
          : "Cancel vote active";

  const resultDescription =
    status === "passed"
      ? "Threshold reached. Match will be voided with no rating changes."
      : status === "failed"
        ? "Not enough yes votes. Match continues."
        : status === "expired"
          ? "Timer ended before threshold was reached. Match continues."
          : `${initiatorName} started a cancel vote.`;

  return (
    <div
      className={cn(
        "rounded-xl border p-4 space-y-3",
        status === "pending" && "border-amber-500/30 bg-amber-500/5",
        status === "passed" && "border-brand-500/30 bg-brand-500/10",
        status !== "pending" && status !== "passed" && "border-error-500/30 bg-error-500/5",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {status === "pending" && <AlertTriangle className="h-4 w-4 text-amber-300 shrink-0" />}
            {status === "passed" && <CheckCircle2 className="h-4 w-4 text-brand-300 shrink-0" />}
            {status !== "pending" && status !== "passed" && (
              <XCircle className="h-4 w-4 text-error-300 shrink-0" />
            )}
            <p className="text-sm font-display font-bold uppercase tracking-wider text-white">
              {resultTitle}
            </p>
          </div>
          <p className="text-sm text-surface-300 mt-1">{resultDescription}</p>
          <p className="text-xs text-surface-400 mt-2">
            Reason: <span className="text-surface-200">{reasonLabels[reason]}</span>
            {reason === "other" && reasonOtherText ? ` - ${reasonOtherText}` : ""}
          </p>
          <p className="text-xs text-surface-500 mt-1 font-mono">Vote #{voteId.slice(0, 8)}</p>
        </div>

        {status === "pending" && (
          <div className="flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs font-semibold text-amber-200">
            <Clock3 className="h-3.5 w-3.5" />
            {secondsRemaining ?? 0}s
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-surface-300">
          <span>
            {totalVotes}/{totalPlayers} voted
          </span>
          <span>
            {yesCount} yes • {noCount} no
          </span>
        </div>
        <ProgressBar value={progressValue} max={totalPlayers} color="yellow" size="lg" />
        <p className="text-xs text-surface-400">
          Need <span className="font-semibold text-white">{requiredYesVotes}</span> yes votes to
          cancel ({yesCount}/{requiredYesVotes})
        </p>
      </div>

      {status === "pending" && (
        <div className="flex items-center gap-2">
          {myVote == null ? (
            <>
              <Button
                variant="danger"
                size="sm"
                disabled={isCastingVote}
                onClick={() => void onVote(true)}
                className="flex-1"
              >
                {isCastingVote ? "Submitting..." : "Yes, Cancel"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={isCastingVote}
                onClick={() => void onVote(false)}
                className="flex-1"
              >
                No, Continue
              </Button>
            </>
          ) : (
            <div className="w-full rounded-md border border-surface-700 bg-surface-800/40 px-3 py-2 text-sm text-surface-200">
              You voted: <span className="font-semibold">{myVote ? "Yes" : "No"}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
